import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { ItemTypeConfig } from "@/pages/unified-items/configs/types";

export interface UseDialogsHandlersProps {
  data: any[];
  selectedGame: any;
  config: ItemTypeConfig;
  deleteDialogItem: (id: string, gameId: string) => Promise<void>;
  createDialogItem: (data: any) => Promise<any>;
  updateDialogItem: (data: any) => Promise<any>;
  setIsAddWizardOpen: (value: boolean) => void;
}

export function useDialogsHandlers({
  data,
  selectedGame,
  config,
  deleteDialogItem,
  setIsAddWizardOpen,
}: UseDialogsHandlersProps) {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    async (id: string, gameId: string) => {
      const item = data.find((item: any) => item.id === id);
      const itemName = item?.name || "this item";

      const confirmDialog = confirmation({
        title: "Delete Dialog",
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "destructive",
        onConfirm: async () => {
          try {
            await deleteDialogItem(id, gameId);
            confirmDialog.dismiss();
          } catch (error) {
            console.error("Error deleting dialog:", error);
            confirmDialog.dismiss();
            toast({
              title: "Error",
              description: "Failed to delete dialog",
              variant: "destructive",
            });
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    },
    [data, confirmation, toast, deleteDialogItem]
  );

  const handleCardClick = useCallback(
    (card: CardData) => {
      navigate(`/dialogs/${card.id}`, { state: { itemName: card.title } });
    },
    [navigate]
  );

  const handleAdd = useCallback(() => {
    // Navigate to new dialog page instead of opening wizard
    navigate('/dialogs/new');
  }, [navigate]);

  return {
    handleDelete,
    handleCardClick,
    handleAdd,
  };
}

